import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Eye, Check, RefreshCw, CheckCircle } from 'lucide-react';
import { PreviewTabla } from './PreviewTabla';

const TONOS = {
  slate: 'bg-slate-50 border-slate-200 text-slate-800',
  blue: 'bg-blue-50 border-blue-200 text-blue-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  rose: 'bg-rose-50 border-rose-200 text-rose-800',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
};

const Totales = ({ totales, defs, testid }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid={`${testid}-totales`}>
    {defs.map((d) => (
      <div key={d.key} className={`p-3 rounded-lg border ${TONOS[d.tono || 'slate']}`}>
        <p className="text-[11px] uppercase tracking-wide opacity-70">{d.label}</p>
        <p className="text-xl font-bold" data-testid={`${testid}-total-${d.key}`}>{totales?.[d.key] ?? 0}</p>
      </div>
    ))}
  </div>
);

export const CvuToolPreview = ({
  testid, titulo, descripcion, icono: Icono, run, disabled, children,
  totalesDefs, tablas, puedeAplicar, textoConfirmacion, resetKey,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aplicado, setAplicado] = useState(false);
  const [confirmar, setConfirmar] = useState(false);

  useEffect(() => { setData(null); setAplicado(false); }, [resetKey]);

  const ejecutar = async (aplicar) => {
    setLoading(true);
    try {
      const res = await run(aplicar);
      setData(res);
      setAplicado(aplicar);
      if (aplicar) toast.success(`Aplicado: ${res?.totales?.aplicados ?? 0} cambios`, { duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar la herramienta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-start gap-3">
        {Icono && <div className="p-2 rounded-lg bg-indigo-50"><Icono size={18} className="text-indigo-600" /></div>}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900">{titulo}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{descripcion}</p>
        </div>
      </div>
      {children && <div className="mt-4">{children}</div>}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button variant="outline" size="sm" disabled={disabled || loading} onClick={() => ejecutar(false)} data-testid={`${testid}-preview-btn`}>
          {loading && !confirmar ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <Eye size={14} className="mr-1.5" />}
          Vista previa
        </Button>
        {data && !aplicado && puedeAplicar(data) && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={loading}
            onClick={() => setConfirmar(true)} data-testid={`${testid}-apply-btn`}>
            <Check size={14} className="mr-1.5" /> Aplicar cambios
          </Button>
        )}
        {data && !aplicado && !puedeAplicar(data) && (
          <span className="text-xs text-slate-500" data-testid={`${testid}-nada`}>No hay cambios para aplicar.</span>
        )}
        {data && !aplicado && <Badge className="bg-amber-100 text-amber-800" data-testid={`${testid}-modo`}>VISTA PREVIA · no se guardó nada</Badge>}
        {aplicado && (
          <span className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid={`${testid}-aplicado`}>
            <CheckCircle size={15} /> Aplicado ({data?.totales?.aplicados ?? 0} cambios)
          </span>
        )}
      </div>
      {data && (
        <div className="mt-4 space-y-4">
          <Totales totales={data.totales} defs={totalesDefs} testid={testid} />
          {tablas.map((t) => (
            <PreviewTabla key={t.key} testid={`${testid}-${t.key}`} titulo={t.titulo} columnas={t.columnas} filas={data[t.key] || []} />
          ))}
        </div>
      )}
      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent data-testid={`${testid}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Aplicar los cambios?</AlertDialogTitle>
            <AlertDialogDescription>{textoConfirmacion}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-confirm-cancel`}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-confirm-ok`} onClick={() => { setConfirmar(false); ejecutar(true); }}>
              Sí, aplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
